import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import {
  Wallet, Plus, CreditCard, TrendingUp, Gift, Star,
  ArrowDown, ArrowUp, Building, User, Check, Copy, Loader2, DollarSign, Banknote, ExternalLink, Share2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAYPAL_OPTIONS = { 
  clientId: "AZmN3SPatPxjzq1IO_5RLJKvCoP6cb3-drUGG1MRSX9Ir26-G1xcW8p3hX6u_SLyahYmIwBo2cv5LLH4",
  currency: "PLN",
  locale: "pl_PL",
  enableFunding: "blik,p24,card"
};

export default function ZorkiWallet() {
  const { user, updateProfile } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [referral, setReferral] = useState(null);
  const [loading, setLoading] = useState(true);
  const [topupAmount, setTopupAmount] = useState(100);
  const [creditAmount, setCreditAmount] = useState(500);
  const [topupDialog, setTopupDialog] = useState(false);
  const [creditDialog, setCreditDialog] = useState(false);
  const [withdrawDialog, setWithdrawDialog] = useState(false);
  const [creditPayDialog, setCreditPayDialog] = useState(false);
  const [withdrawals, setWithdrawals] = useState([]);
  const [withdrawForm, setWithdrawForm] = useState({ amount: 100, method: "paypal", address: "" });
  const [withdrawing, setWithdrawing] = useState(false);
  const [profileForm, setProfileForm] = useState({
    display_name: "", author_display_name: "", company_name: "", nip: "", address: "", phone: ""
  });

  const fetchData = useCallback(async () => {
    try {
      const [walletRes, txRes, refRes, wRes] = await Promise.all([
        axios.get(`${API}/zorki/balance`),
        axios.get(`${API}/zorki/transactions`),
        axios.get(`${API}/referral/link`),
        axios.get(`${API}/zorki/withdrawals`),
      ]);
      setWallet(walletRes.data);
      setTransactions(txRes.data);
      setReferral(refRes.data);
      setWithdrawals(wRes.data);
      if (user) {
        setProfileForm({
          display_name: user.display_name || "", author_display_name: user.author_display_name || "", company_name: user.company_name || "",
          nip: user.nip || "", address: user.address || "", phone: user.phone || ""
        });
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [user]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleTopup = async () => {
    try {
      await axios.post(`${API}/zorki/topup`, { amount: topupAmount });
      toast.success(`Doladowano ${topupAmount} Zorkow!`);
      setTopupDialog(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Blad"); }
  };

  const handleCredit = async () => {
    try {
      await axios.post(`${API}/zorki/credit`, { amount: creditAmount });
      toast.success(`Kredyt kupiecki ${creditAmount} Zorkow aktywowany!`);
      setCreditDialog(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Blad - uzupelnij profil firmy"); }
  };

  const handleWithdraw = async () => {
    setWithdrawing(true);
    try {
      const res = await axios.post(`${API}/zorki/withdraw`, withdrawForm);
      toast.success(res.data.message);
      setWithdrawDialog(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Blad wyplaty"); }
    finally { setWithdrawing(false); }
  };

  const handleSaveProfile = async () => {
    try {
      await updateProfile(profileForm);
      toast.success("Profil zapisany");
      fetchData();
    } catch (err) { toast.error("Blad zapisu"); }
  };

  const balance = wallet?.balance || 0;
  const creditLimit = wallet?.credit_limit || 0;
  const creditUsed = wallet?.credit_used || 0;
  const creditAvail = creditLimit - creditUsed;
  const total = balance + creditAvail;
  const creditOverdue = wallet?.credit_overdue || false;
  const creditOverdueDays = wallet?.credit_overdue_days || 0;

  const handleBlikPayment = async (amount, purpose = "topup") => {
    try {
      const res = await axios.post(`${API}/paypal/create-order`, { 
        amount, payment_method: "blik", purpose 
      });
      if (res.data.approve_url) {
        if (purpose === "credit_payment") localStorage.setItem('pending_credit_payment', amount.toString());
        window.open(res.data.approve_url, "_blank");
      }
    } catch (err) { toast.error("Blad BLIK"); }
  };

  const handleP24Payment = async (amount, purpose = "topup") => {
    try {
      const res = await axios.post(`${API}/paypal/create-order`, { 
        amount, payment_method: "p24", purpose 
      });
      if (res.data.approve_url) {
        if (purpose === "credit_payment") localStorage.setItem('pending_credit_payment', amount.toString());
        window.open(res.data.approve_url, "_blank");
      }
    } catch (err) { toast.error("Blad Przelewy24"); }
  };

  const handleCardPayment = async (amount, purpose = "topup") => {
    try {
      const res = await axios.post(`${API}/paypal/create-order`, { 
        amount, payment_method: "card", purpose 
      });
      if (res.data.approve_url) {
        if (purpose === "credit_payment") localStorage.setItem('pending_credit_payment', amount.toString());
        window.open(res.data.approve_url, "_blank");
      }
    } catch (err) { toast.error("Blad platnosci karta"); }
  };

  return (
    <PayPalScriptProvider options={PAYPAL_OPTIONS}>
      <div data-testid="zorki-page" className="space-y-6">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-foreground">Portfel Zorkow</h1>
          <p className="text-sm text-muted-foreground mt-1">1 Zorek = 1 PLN. Doladuj konto i zamawiaj publikacje.</p>
        </div>

        {creditUsed > 0 && (
          <Card className={`rounded-2xl border-2 ${creditOverdue ? 'border-red-300 bg-red-50' : 'border-amber-200 bg-amber-50'}`}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${creditOverdue ? 'bg-red-100' : 'bg-amber-100'}`}>
                    <CreditCard className={`w-6 h-6 ${creditOverdue ? 'text-red-600' : 'text-amber-600'}`} />
                  </div>
                  <div>
                    <p className={`font-bold ${creditOverdue ? 'text-red-700' : 'text-amber-700'}`}>
                      {creditOverdue ? 'Zaleglosc kredytu kupieckiego!' : 'Kredyt kupiecki do splacenia'}
                    </p>
                    <p className={`text-sm ${creditOverdue ? 'text-red-600' : 'text-amber-600'}`}>
                      Masz <strong>{creditUsed} PLN</strong> do splacenia.
                      {creditOverdue ? ` Termin minal ${creditOverdueDays} dni temu.` : ' Termin: do 10-go dnia miesiaca.'}
                    </p>
                  </div>
                </div>
                <Button 
                  onClick={() => setCreditPayDialog(true)}
                  className={`gap-2 h-12 px-6 ${creditOverdue ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'} text-white`}
                >
                  <CreditCard className="w-4 h-4" />
                  Splac teraz ({creditUsed} PLN)
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <Dialog open={creditPayDialog} onOpenChange={setCreditPayDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-center">Splac kredyt kupiecki</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="text-center p-4 bg-slate-50 rounded-xl">
                <p className="text-sm text-slate-500">Kwota do splacenia</p>
                <p className="text-4xl font-bold text-slate-900">{creditUsed} <span className="text-lg">PLN</span></p>
              </div>
              
              <Separator />
              
              <div className="space-y-2">
                <PayPalButtons
                  style={{ layout: "vertical", color: "gold", shape: "rect", label: "paypal", height: 48 }}
                  createOrder={async () => {
                    const res = await axios.post(`${API}/paypal/create-order`, { 
                      amount: creditUsed, payment_method: "paypal", purpose: "credit_payment"
                    });
                    return res.data.order_id;
                  }}
                  onApprove={async (data) => {
                    try {
                      const res = await axios.post(`${API}/paypal/capture-order`, { order_id: data.orderID });
                      if (res.data.success) {
                        await axios.post(`${API}/zorki/pay-credit`, { amount: creditUsed });
                        toast.success(`Splacono kredyt ${creditUsed} PLN!`);
                        setCreditPayDialog(false);
                        fetchData();
                      }
                    } catch (err) { toast.error("Blad przetwarzania platnosci"); }
                  }}
                  onError={() => toast.error("Platnosc nie powiodla sie")}
                />
                
                <button onClick={() => handleBlikPayment(creditUsed, "credit_payment")}
                  className="w-full h-12 bg-black text-white font-bold rounded-md flex items-center justify-center hover:bg-gray-800">
                  <span className="text-lg font-black tracking-wider">BLIK</span>
                </button>
                
                <button onClick={() => handleP24Payment(creditUsed, "credit_payment")}
                  className="w-full h-12 bg-[#d32f2f] text-white font-bold rounded-md flex items-center justify-center hover:bg-[#b71c1c]">
                  <span className="text-sm font-bold">Przelewy24</span>
                </button>
                
                <button onClick={() => handleCardPayment(creditUsed, "credit_payment")}
                  className="w-full h-12 bg-gray-700 text-white font-medium rounded-md flex items-center justify-center gap-2 hover:bg-gray-600">
                  <CreditCard className="w-4 h-4" />
                  <span className="text-sm">Karta debetowa lub kredytowa</span>
                </button>
              </div>
              
              <p className="text-[10px] text-center text-slate-400 mt-2">
                Powered by <span className="font-bold text-blue-600">PayPal</span>
              </p>
            </div>
          </DialogContent>
        </Dialog>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="md:col-span-2 rounded-2xl border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-white">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-2">
                <Star className="w-6 h-6 text-blue-600 fill-blue-600" />
                <span className="text-sm text-blue-600 font-medium">Saldo Zorkow</span>
              </div>
              <p className="text-5xl font-bold text-slate-900">{balance}</p>
              <p className="text-sm text-slate-500 mt-1">Dostepne do wydania: <strong>{total}</strong> (w tym {creditAvail} kredytu)</p>
              {creditUsed > 0 && (
                <p className={`text-xs mt-1 ${creditOverdue ? 'text-red-600 font-bold' : 'text-amber-600'}`}>
                  Do splacenia: {creditUsed} PLN {creditOverdue && '(PO TERMINIE!)'}
                </p>
              )}
              <div className="flex gap-2 mt-4">
                <Dialog open={topupDialog} onOpenChange={setTopupDialog}>
                  <DialogTrigger asChild>
                    <Button className="gap-2 rounded-xl h-11 bg-blue-600"><Plus className="w-4 h-4" /> Doladuj Zorki</Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader><DialogTitle>Doladuj Zorki (1 Zorek = 1 PLN)</DialogTitle></DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="grid grid-cols-4 gap-2">
                        {[30, 50, 100, 200].map(a => (
                          <button key={a} onClick={() => setTopupAmount(a)}
                            className={`p-3 rounded-xl border-2 text-center font-bold transition-all ${topupAmount === a ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-600"}`}>
                            {a}
                          </button>
                        ))}
                      </div>
                      <div className="relative">
                        <Input type="number" value={topupAmount} onChange={(e) => setTopupAmount(parseInt(e.target.value) || 30)}
                          className="h-14 text-center text-3xl font-bold pr-12" min={10} />
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-slate-400">PLN</span>
                      </div>
                      
                      <Separator />
                      
                      <div className="space-y-2">
                        <PayPalButtons
                          style={{ layout: "vertical", color: "gold", shape: "rect", label: "paypal", height: 48 }}
                          createOrder={async () => {
                            const res = await axios.post(`${API}/paypal/create-order`, { 
                              amount: topupAmount, payment_method: "paypal" 
                            });
                            return res.data.order_id;
                          }}
                          onApprove={async (data) => {
                            try {
                              const res = await axios.post(`${API}/paypal/capture-order`, { order_id: data.orderID });
                              if (res.data.success) {
                                toast.success(`Doladowano ${res.data.amount} Zorkow!`);
                                setTopupDialog(false);
                                fetchData();
                              }
                            } catch (err) { toast.error("Blad przetwarzania platnosci"); }
                          }}
                          onError={() => toast.error("Platnosc nie powiodla sie")}
                        />
                        
                        <button onClick={() => handleBlikPayment(topupAmount)}
                          className="w-full h-12 bg-black text-white font-bold rounded-md flex items-center justify-center hover:bg-gray-800">
                          <span className="text-lg font-black tracking-wider">BLIK</span>
                        </button>
                        
                        <button onClick={() => handleP24Payment(topupAmount)}
                          className="w-full h-12 bg-[#d32f2f] text-white font-bold rounded-md flex items-center justify-center hover:bg-[#b71c1c]">
                          <span className="text-sm font-bold">Przelewy24</span>
                        </button>
                        
                        <button onClick={() => handleCardPayment(topupAmount)}
                          className="w-full h-12 bg-gray-700 text-white font-medium rounded-md flex items-center justify-center gap-2 hover:bg-gray-600">
                          <CreditCard className="w-4 h-4" />
                          <span className="text-sm">Karta debetowa lub kredytowa</span>
                        </button>
                      </div>
                      
                      <p className="text-[10px] text-center text-slate-400 mt-2">
                        Powered by <span className="font-bold text-blue-600">PayPal</span>
                      </p>
                    </div>
                  </DialogContent>
                </Dialog>

                <Dialog open={withdrawDialog} onOpenChange={setWithdrawDialog}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="gap-2 rounded-xl h-11" disabled={balance < 50}>
                      <Banknote className="w-4 h-4" /> Wyplac
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-sm">
                    <DialogHeader><DialogTitle>Wyplata Zorkow</DialogTitle></DialogHeader>
                    <div className="space-y-3 py-4">
                      <p className="text-xs text-slate-500">Min. 50 Zorkow. Wyplata po zatwierdzeniu przez admina (do 24h).</p>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Kwota (Zorkow)</Label>
                        <Input type="number" value={withdrawForm.amount} onChange={(e) => setWithdrawForm({...withdrawForm, amount: parseInt(e.target.value) || 0})} className="h-11 text-xl font-bold text-center" />
                        <p className="text-[10px] text-slate-400">Dostepne: {balance} Zorkow</p>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Metoda wyplaty</Label>
                        <Select value={withdrawForm.method} onValueChange={(v) => setWithdrawForm({...withdrawForm, method: v})}>
                          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="paypal">PayPal</SelectItem>
                            <SelectItem value="usdt">USDT (crypto)</SelectItem>
                            <SelectItem value="usdc">USDC (crypto)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">{withdrawForm.method === "paypal" ? "Email PayPal" : "Adres portfela"}</Label>
                        <Input value={withdrawForm.address} onChange={(e) => setWithdrawForm({...withdrawForm, address: e.target.value})}
                          placeholder={withdrawForm.method === "paypal" ? "twoj@email.com" : "0x..."} />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button className="w-full h-11 bg-emerald-600" onClick={handleWithdraw} disabled={withdrawing || withdrawForm.amount < 50}>
                        {withdrawing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Wyplac {withdrawForm.amount} Zorkow = {withdrawForm.amount} PLN
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl">
            <CardContent className="p-5">
              <CreditCard className="w-5 h-5 text-amber-500 mb-2" />
              <p className="text-2xl font-bold">{creditLimit}</p>
              <p className="text-xs text-slate-500">Kredyt kupiecki</p>
              <p className="text-[10px] text-slate-400">Wykorzystano: {creditUsed}</p>
              <Dialog open={creditDialog} onOpenChange={setCreditDialog}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" className="mt-2 w-full text-xs">
                    {creditLimit > 0 ? "Zmien limit" : "Aktywuj kredyt"}
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-sm">
                  <DialogHeader><DialogTitle>Kredyt kupiecki</DialogTitle></DialogHeader>
                  <p className="text-xs text-slate-500">Wymaga uzupelnionych danych firmy. Termin: do 10 kazdego miesiaca.</p>
                  <div className="grid grid-cols-4 gap-2 py-4">
                    {[500, 1000, 2000, 5000].map(a => (
                      <button key={a} onClick={() => setCreditAmount(a)}
                        className={`p-3 rounded-xl border-2 text-sm font-bold ${creditAmount === a ? "border-amber-500 bg-amber-50" : "border-slate-200"}`}>
                        {a}
                      </button>
                    ))}
                  </div>
                  <Button className="w-full h-11 bg-amber-600" onClick={handleCredit}>Aktywuj {creditAmount} Zorkow kredytu</Button>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-emerald-200 bg-gradient-to-br from-emerald-50 to-white">
            <CardContent className="p-5">
              <Gift className="w-5 h-5 text-emerald-500 mb-2" />
              <p className="text-sm font-bold text-slate-900">Program polecen</p>
              <p className="text-xs text-slate-500 mt-1">
                <span className="font-bold text-emerald-600">50 Zorkow</span> za kazdego poleconego uzytkownika
              </p>
              {referral && (
                <>
                  <div className="mt-3 p-2 bg-white rounded-lg border border-emerald-100">
                    <p className="text-[10px] text-slate-400 mb-1">Twoj link polecajacy:</p>
                    <p className="text-xs font-mono text-slate-700 truncate">{referral.referral_link}</p>
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Button variant="outline" size="sm" className="flex-1 text-xs gap-1 h-8"
                      onClick={() => { navigator.clipboard.writeText(referral.referral_link); toast.success("Link skopiowany!"); }}>
                      <Copy className="w-3 h-3" /> Kopiuj
                    </Button>
                    <Button variant="outline" size="sm" className="text-xs gap-1 h-8"
                      onClick={() => { 
                        const text = `Dolacz do Epidemos i otrzymaj bonus! ${referral.referral_link}`;
                        if (navigator.share) {
                          navigator.share({ title: 'Epidemos', text: text, url: referral.referral_link });
                        } else {
                          navigator.clipboard.writeText(text); 
                          toast.success("Skopiowano tekst do udostepnienia!");
                        }
                      }}>
                      <Share2 className="w-3 h-3" />
                    </Button>
                  </div>
                  <div className="mt-3 pt-3 border-t border-emerald-100 grid grid-cols-2 gap-2 text-center">
                    <div>
                      <p className="text-2xl font-bold text-emerald-600">{referral.referrals_count || 0}</p>
                      <p className="text-[10px] text-slate-500">Poleconych</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-emerald-600">{referral.total_earned || 0}</p>
                      <p className="text-[10px] text-slate-500">Zarobionych Zorkow</p>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="rounded-2xl">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2"><Building className="w-4 h-4" /> Dane firmy (do faktury i kredytu)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Imie i nazwisko</Label>
                  <Input value={profileForm.display_name} onChange={(e) => setProfileForm({...profileForm, display_name: e.target.value})} /></div>
                <div className="space-y-1"><Label className="text-xs">Telefon</Label>
                  <Input value={profileForm.phone} onChange={(e) => setProfileForm({...profileForm, phone: e.target.value})} /></div>
              </div>
              <div className="space-y-1"><Label className="text-xs">Podpis autora (wyswietlany pod artykulami)</Label>
                <Input value={profileForm.author_display_name} onChange={(e) => setProfileForm({...profileForm, author_display_name: e.target.value})} placeholder="np. Jan Kowalski - Ekspert SEO" /></div>
              <div className="space-y-1"><Label className="text-xs">Nazwa firmy</Label>
                <Input value={profileForm.company_name} onChange={(e) => setProfileForm({...profileForm, company_name: e.target.value})} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">NIP</Label>
                  <Input value={profileForm.nip} onChange={(e) => setProfileForm({...profileForm, nip: e.target.value})} /></div>
                <div className="space-y-1"><Label className="text-xs">Adres</Label>
                  <Input value={profileForm.address} onChange={(e) => setProfileForm({...profileForm, address: e.target.value})} /></div>
              </div>
              <Button onClick={handleSaveProfile} className="w-full rounded-xl">Zapisz profil</Button>
              {user?.profile_complete && <Badge className="bg-emerald-100 text-emerald-700 border-0"><Check className="w-3 h-3 mr-1" /> Profil kompletny - kredyt dostepny</Badge>}
            </CardContent>
          </Card>

          <Card className="rounded-2xl">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Historia transakcji</CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[300px]">
                {transactions.length === 0 ? (
                  <p className="text-xs text-slate-400 text-center py-8">Brak transakcji</p>
                ) : (
                  <div className="space-y-2">
                    {transactions.map((tx) => (
                      <div key={tx.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${tx.amount > 0 ? "bg-emerald-100" : "bg-red-100"}`}>
                          {tx.amount > 0 ? <ArrowDown className="w-4 h-4 text-emerald-600" /> : <ArrowUp className="w-4 h-4 text-red-600" />}
                        </div>
                        <div className="flex-1">
                          <p className="text-xs font-medium">{tx.description}</p>
                          <p className="text-[9px] text-slate-400">{new Date(tx.created_at).toLocaleString()}</p>
                        </div>
                        <span className={`text-sm font-bold ${tx.amount > 0 ? "text-emerald-600" : "text-red-600"}`}>
                          {tx.amount > 0 ? "+" : ""}{tx.amount}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>
    </PayPalScriptProvider>
  );
}
